import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HistoryView } from '../../views'
import { HistoryService, UserService, LocalStorageService } from '../../../services'
import { useGlobalState } from '../../../GlobalState'
import moment from 'moment'

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 10
const SEARCH_DEBOUNCE_MS = 700
// Debe coincidir con sortOptions[0].value de conversation-list.js.
const DEFAULT_SORT = 'recent'

const EMPTY_FILTERS = { dateStart: null, dateEnd: null, timeStart: null, timeEnd: null, sentiments: [], historyStatuses: [], userId: null }

// Los pickers manejan objetos Date; el servicio espera cadenas normalizadas.
const buildAppliedFilters = (draft) => ({
  dateStart: draft.dateStart ? moment(draft.dateStart).format('YYYY-MM-DD') : null,
  dateEnd: draft.dateEnd ? moment(draft.dateEnd).format('YYYY-MM-DD') : null,
  timeStart: draft.timeStart ? moment(draft.timeStart).format('HH:mm') : null,
  timeEnd: draft.timeEnd ? moment(draft.timeEnd).format('HH:mm') : null,
  sentiments: Array.isArray(draft.sentiments) ? draft.sentiments : [],
  historyStatuses: Array.isArray(draft.historyStatuses) ? draft.historyStatuses : [],
  userId: draft.userId || null
})

// Preselección al llegar desde las tarjetas de sentimiento (usage.view): mes completo del periodo
// y el sentimiento elegido, con objetos Date para los pickers.
const buildPresetDraft = (preset) => ({
  dateStart: moment(String(preset.period), 'YYYYMM').startOf('month').toDate(),
  dateEnd: moment(String(preset.period), 'YYYYMM').endOf('month').toDate(),
  timeStart: null,
  timeEnd: null,
  sentiments: [preset.sentiment],
  historyStatuses: [],
  userId: null
})

const HistoryAction = () => {
  const url = process.env.REACT_APP_ETICKET_API
  const historyService = new HistoryService(url)
  const userService = new UserService(url)
  const localStorageService = new LocalStorageService()
  const token = localStorageService.getLocalStorageItem('token')?.accessToken
  const clientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData?.userA3Cloud.clientId
  const chatbotUrl = process.env.REACT_APP_CHATBOT_URL
  const { botConfig } = useGlobalState()

  // Preselección enviada desde las tarjetas de sentimiento (usage.view) vía router state.
  const location = useLocation()
  const preset = location.state?.advancedSearch
  const presetDraft = preset ? buildPresetDraft(preset) : null

  const [dataConversations, setDataConversations] = useState(null)
  const [initialDataConversations, setInitialDataConversations] = useState(null)
  const [initialQueryParams, setInitialQueryParams] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingChangePage, setIsLoadingChangePage] = useState(true)
  const [date, setDate] = useState(preset?.period ? parseInt(preset.period) : parseInt(moment().format('YYYYMM')))
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE)
  const [currentLimit, setCurrentLimit] = useState(DEFAULT_LIMIT)
  const [currentSort, setCurrentSort] = useState(DEFAULT_SORT)
  const [advancedOpen, setAdvancedOpen] = useState(Boolean(preset))
  const [filterDraft, setFilterDraft] = useState(presetDraft || EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(presetDraft ? buildAppliedFilters(presetDraft) : EMPTY_FILTERS)
  const [statusCatalog, setStatusCatalog] = useState([])
  const [usersCatalog, setUsersCatalog] = useState([])

  const monthYearOptions = Array.from({ length: 12 }, (_, i) => {
      const monthDate = moment().subtract(i, 'months')
      const value = parseInt(monthDate.format('YYYYMM'))
      return {
          value,
          text: monthDate.format('MMMM YYYY')
      }
  }).reverse()

  const handleChangeDate = (event) => {
      setDate(event.target.value.value)
  }

  const toggleAdvanced = () => {
    setAdvancedOpen(prev => {
      const next = !prev
      if (!next) {
        // Al cerrar Búsqueda Avanzada se vuelve al modo dropdown de mes.
        setFilterDraft(EMPTY_FILTERS)
        setAppliedFilters(EMPTY_FILTERS)
      }
      return next
    })
  }

  const handleFilterChange = (field, value) => {
    setFilterDraft(prev => ({ ...prev, [field]: value }))
  }

  const handleToggleSentiment = (id) => {
    setFilterDraft(prev => {
      const isSelected = prev.sentiments.includes(id)
      return {
        ...prev,
        sentiments: isSelected
          ? prev.sentiments.filter(sentiment => sentiment !== id)
          : [...prev.sentiments, id]
      }
    })
  }

  const handleToggleHistoryStatus = (id) => {
    setFilterDraft(prev => {
      const isSelected = prev.historyStatuses.includes(id)
      return {
        ...prev,
        historyStatuses: isSelected
          ? prev.historyStatuses.filter(status => status !== id)
          : [...prev.historyStatuses, id]
      }
    })
  }

  const applyFilters = () => {
    setAppliedFilters(buildAppliedFilters(filterDraft))
  }

  const clearFilters = () => {
    setFilterDraft(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
  }

  useEffect(() => {
    const loadStatusCatalog = async () => {
      try {
        const catalog = await historyService.getHistoryStatusCatalog(token)
        setStatusCatalog(Array.isArray(catalog) ? catalog : [])
      } catch (error) {
        console.error(`An error has ocurred loading history status catalog: ${error}`)
      }
    }

    loadStatusCatalog()
  }, [])

  useEffect(() => {
    const loadUsersCatalog = async () => {
      try {
        const users = await userService.getUsersCollaboratorByClientId(clientId, token)
        const options = Array.isArray(users)
          ? users.map(user => ({ text: user.fullName, value: user.id }))
          : []
        setUsersCatalog(options)
      } catch (error) {
        console.error(`An error has ocurred loading users catalog: ${error}`)
      }
    }

    if (clientId) {
      loadUsersCatalog()
    }
  }, [clientId])

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim())
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(debounceTimeout)
  }, [searchValue])

  useEffect(() => {
    if (debouncedSearchValue) {
      handleSearchConversations(DEFAULT_PAGE, currentLimit, currentSort, debouncedSearchValue, appliedFilters)
      return
    }

    handleLoadInitialConversations(DEFAULT_PAGE, currentLimit, currentSort, appliedFilters)
  }, [date, botConfig?.id, appliedFilters])

  useEffect(() => {
    if (!debouncedSearchValue) {
      const canRestoreInitialData = Boolean(
        initialDataConversations
        && initialQueryParams
        && initialQueryParams.page === currentPage
        && initialQueryParams.limit === currentLimit
        && initialQueryParams.sort === currentSort
        && initialQueryParams.date === date
        && initialQueryParams.botId === (botConfig?.id || null)
        && JSON.stringify(initialQueryParams.filters) === JSON.stringify(appliedFilters)
      )

      if (canRestoreInitialData) {
        setDataConversations(initialDataConversations)
        setCurrentPage(initialDataConversations.page || DEFAULT_PAGE)
        setIsLoading(false)
        setIsLoadingChangePage(false)
      } else if (!isLoading) {
        handleLoadInitialConversations(currentPage, currentLimit, currentSort, appliedFilters)
      }
      return
    }

    handleSearchConversations(DEFAULT_PAGE, currentLimit, currentSort, debouncedSearchValue, appliedFilters)
  }, [debouncedSearchValue])
  
  /**
   * @description method to get paginated conversation
   * @param page page number
   * @param limit number of registers by page
   */
  const handleLoadInitialConversations = async (page, limit, sort = null, filters = appliedFilters) => {
    try {
      setIsLoadingChangePage(true)
      let monthNumber = date % 100
      let yearNumber = Math.floor(date / 100)

      // Cuando hay rango de fechas (búsqueda avanzada) derivamos year/month de dateStart.
      if (filters?.dateStart) {
        const start = moment(filters.dateStart)
        yearNumber = start.year()
        monthNumber = start.month() + 1
      }

      const response = await historyService.getConversationsByDateAndPageLimit(yearNumber, monthNumber, token, null, botConfig?.id || null, page, limit, sort, filters)
      setInitialDataConversations(response)
      setInitialQueryParams({
        page,
        limit,
        sort,
        date,
        botId: botConfig?.id || null,
        filters
      })
      setDataConversations(response)
      setCurrentPage(page)
      setCurrentLimit(limit)
      setCurrentSort(sort)
      setIsLoadingChangePage(false)
      isLoading && setIsLoading(false)
    } catch (error) {
      console.error(`An error has ocurred in usageAction: ${error}`)
      setIsLoadingChangePage(false)
    }
  }

  const handleSearchConversations = async (page, limit, sort = null, search = debouncedSearchValue, filters = appliedFilters) => {
    try {
      setIsLoadingChangePage(true)
      let monthNumber = date % 100
      let yearNumber = Math.floor(date / 100)

      // En modo avanzado el criterio temporal es el rango; derivamos year/month de dateStart
      // para satisfacer el contrato del endpoint (que prioriza dateStart/dateEnd cuando existen).
      if (filters?.dateStart) {
        const start = moment(filters.dateStart)
        yearNumber = start.year()
        monthNumber = start.month() + 1
      }

      const response = await historyService.searchConversationsByMessage(search, yearNumber, monthNumber, token, null, botConfig?.id || null, page, limit, sort, filters)
      setDataConversations(response)
      setCurrentPage(page)
      setCurrentLimit(limit)
      setCurrentSort(sort)
      setIsLoadingChangePage(false)
      isLoading && setIsLoading(false)
    } catch (error) {
      console.error(`An error has ocurred in historySearchAction: ${error}`)
      setIsLoadingChangePage(false)
    }
  }

  const handleGetPaginatedConversation = async (page, limit, sort = null) => {
    if (debouncedSearchValue) {
      await handleSearchConversations(page, limit, sort, debouncedSearchValue, appliedFilters)
      return
    }

    await handleLoadInitialConversations(page, limit, sort, appliedFilters)
  }

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value)
  }

  return (
    <HistoryView
      isLoading={isLoading}
      isLoadingChangePage={isLoadingChangePage}
      dataConversations={dataConversations}
      date={date}
      handleChangeDate={handleChangeDate}
      monthYearOptions={monthYearOptions}
      chatbotUrl={chatbotUrl}
      handleGetPaginatedConversation={handleGetPaginatedConversation}
      searchValue={searchValue}
      handleSearchChange={handleSearchChange}
      advancedOpen={advancedOpen}
      filterDraft={filterDraft}
      toggleAdvanced={toggleAdvanced}
      handleFilterChange={handleFilterChange}
      handleToggleSentiment={handleToggleSentiment}
      statusCatalog={statusCatalog}
      usersCatalog={usersCatalog}
      handleToggleHistoryStatus={handleToggleHistoryStatus}
      applyFilters={applyFilters}
      clearFilters={clearFilters}
    />
  )
}

export { HistoryAction as default }